import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, Search, Filter, Download, Plus, Loader2, X, Edit2, Database,
  CheckCircle2, AlertTriangle, ChevronRight, Mail, Building2, Globe,
  Tag, Layers, Calendar, RefreshCcw,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import {
  listCustomers, getCustomerStats, getCustomerEnums, patchCustomer,
  createCustomer, previewCustomerBackfill, runCustomerBackfill,
  exportCustomersXlsxUrl,
} from '../../lib/api';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';

const PER_PAGE = 100;
const formatDate = (iso) => {
  if (!iso) return '\u2014';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
};

// ─── KPI tile — Round 1 compact density ─────────────────────────────
const Kpi = ({ label, value, testid }) => (
  <div className="px-3 py-2 bg-[#0A0A0A] border border-white/10" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-widest text-white/40 leading-tight">{label}</div>
    <div className="text-xl font-bold text-white mt-0.5 leading-none">{value ?? '\u2014'}</div>
  </div>
);

// Default empty form so React never sees undefined → defined transitions
// on controlled inputs (avoids the "uncontrolled to controlled" warning
// when the drawer opens before the useEffect has populated form state).
const _EMPTY_FORM = {
  company: '', contact_name: '', phone: '', website: '',
  stage: 'New', language: 'Unknown', segment: '', country: '',
  market: '', import_batch: '', email_opt_in: true, notes: '',
};

// ─── Edit drawer ─────────────────────────────────────────────────────
const EditDrawer = ({ open, customer, enums, onClose, onSaved }) => {
  const [form, setForm] = useState(_EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (customer) {
      setForm({
        company: customer.company || '',
        contact_name: customer.contact_name || '',
        phone: customer.phone || '',
        website: customer.website || '',
        stage: customer.stage || 'New',
        language: customer.language || 'Unknown',
        segment: customer.segment || '',
        country: customer.country || '',
        market: customer.market || '',
        import_batch: customer.import_batch || '',
        email_opt_in: !!customer.email_opt_in,
        notes: customer.notes || '',
      });
    } else {
      setForm(_EMPTY_FORM);
    }
  }, [customer]);
  if (!open || !customer) return null;
  const save = async () => {
    setSaving(true);
    try {
      const r = await patchCustomer(customer.id, form);
      toast.success('Customer updated');
      onSaved?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };
  const fld = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-xl bg-[#0A0A0A] border-l border-white/10 h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-edit-drawer">
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">Edit Customer</div>
            <div className="text-lg font-bold text-white truncate">{customer.company || customer.email || '(no name)'}</div>
            <div className="text-[10px] text-white/40 font-mono">{customer.id}</div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* Source chips */}
          <div className="flex flex-wrap gap-1.5">
            {(customer.sources || []).map((s) => (
              <span key={s} className="px-2 py-0.5 text-[10px] border border-[#00D4FF]/30 text-[#00D4FF] bg-[#00D4FF]/10" data-testid={`source-chip-${s}`}>{s}</span>
            ))}
            {(customer.sources || []).length === 0 && <span className="text-[10px] text-white/30">No sources</span>}
          </div>
          {/* Email is read-only — primary dedupe key */}
          <Field label="Email (read-only — dedupe key)">
            <input value={customer.email || ''} readOnly className="w-full px-3 py-2 bg-black border border-white/10 text-white/60 text-sm font-mono" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Company"><input value={form.company} onChange={fld('company')} className="ip" data-testid="ip-company" /></Field>
            <Field label="Contact Name"><input value={form.contact_name} onChange={fld('contact_name')} className="ip" data-testid="ip-contact-name" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><input value={form.phone} onChange={fld('phone')} className="ip" data-testid="ip-phone" /></Field>
            <Field label="Website"><input value={form.website} onChange={fld('website')} className="ip" data-testid="ip-website" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Stage">
              <select value={form.stage} onChange={fld('stage')} className="ip" data-testid="ip-stage">
                {(enums?.stages || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Language">
              <select value={form.language} onChange={fld('language')} className="ip" data-testid="ip-language">
                {(enums?.languages || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Segment"><input value={form.segment} onChange={fld('segment')} className="ip" /></Field>
            <Field label="Country"><input value={form.country} onChange={fld('country')} className="ip" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Market"><input value={form.market} onChange={fld('market')} className="ip" /></Field>
            <Field label="Import Batch"><input value={form.import_batch} onChange={fld('import_batch')} className="ip" data-testid="ip-import-batch" /></Field>
          </div>
          <Field label="Email Opt-In">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="ip-opt-in">
              <input type="checkbox" checked={form.email_opt_in} onChange={(e) => setForm({ ...form, email_opt_in: e.target.checked })} />
              <span className="text-white/70 text-sm">{form.email_opt_in ? 'Opted in' : 'Opted out'}</span>
            </label>
          </Field>
          <Field label="Notes"><textarea value={form.notes} onChange={fld('notes')} rows={4} className="ip" data-testid="ip-notes" /></Field>

          {/* Phase 4 — Read-only activity & provenance block. Surfaces
              the same fields the Recipient Preview hygiene uses, so
              admins can audit what the campaign engine knows about a
              customer without leaving the drawer. */}
          <div className="mt-2 p-3 border border-white/10 bg-black/40" data-testid="drawer-activity-block">
            <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2">Activity & Provenance</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
              <RoLine label="First Seen" value={fmt(customer.created_at)} testid="ro-first-seen" />
              <RoLine label="Last Seen" value={fmt(customer.updated_at)} testid="ro-last-seen" />
              <RoLine label="Last Contact" value={fmt(customer.last_contact)} testid="ro-last-contact" />
              <RoLine label="Last Campaign Sent" value={fmt(customer.last_campaign_sent)} testid="ro-last-campaign-sent" />
              <RoLine label="Last Campaign ID" value={customer.last_campaign_id || '\u2014'} mono testid="ro-last-campaign-id" />
              <RoLine label="Total Campaigns" value={customer.total_campaigns_received ?? 0} testid="ro-total-campaigns" />
              <RoLine label="Sources" value={(customer.sources || []).join(', ') || '\u2014'} testid="ro-sources" />
              <RoLine label="Import Batch" value={customer.import_batch || '\u2014'} testid="ro-import-batch" />
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-[#0A0A0A] border-t border-white/10 px-5 py-3 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 text-sm bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/30 disabled:opacity-40" data-testid="drawer-save">
            {saving && <Loader2 className="w-4 h-4 inline mr-1 animate-spin" />}
            Save
          </button>
        </div>
        <style>{`.ip { width:100%; padding:.5rem .75rem; background:#000; border:1px solid rgba(255,255,255,.10); color:#fff; font-size:.875rem; outline:none; } .ip:focus { border-color:rgba(0,212,255,.5); }`}</style>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
    {children}
  </div>
);

// Read-only key/value row used inside the activity panel of the
// customer drawer.
const RoLine = ({ label, value, mono, testid }) => (
  <div className="flex items-baseline justify-between gap-3 py-0.5 border-b border-white/5 last:border-b-0" data-testid={testid}>
    <span className="text-white/40 text-[10px] uppercase tracking-wider">{label}</span>
    <span className={`text-right ${mono ? 'font-mono' : ''} text-white/80 max-w-[60%] truncate`} title={String(value)}>{value}</span>
  </div>
);

// ISO → readable date+time (UTC display). Returns em-dash when empty.
const fmt = (iso) => {
  if (!iso) return '\u2014';
  try { return new Date(iso).toISOString().replace('T', ' ').slice(0, 16); }
  catch { return iso; }
};

// ─── Create modal ────────────────────────────────────────────────────
const CreateModal = ({ open, enums, onClose, onCreated }) => {
  const [form, setForm] = useState({
    company: '', contact_name: '', email: '', phone: '', website: '',
    stage: 'New', language: 'Unknown', segment: '', country: '',
    import_batch: '', email_opt_in: true, notes: '',
  });
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  const save = async () => {
    if (!form.company && !form.email) { toast.error('Email or Company is required.'); return; }
    setSaving(true);
    try {
      const r = await createCustomer(form);
      if (r.data.merged) toast.success('Merged into existing customer (dedupe matched)');
      else toast.success('Customer created');
      onCreated?.(r.data.customer);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    } finally { setSaving(false); }
  };
  const fld = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-customer-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">New Customer</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Company"><input value={form.company} onChange={fld('company')} className="ip" data-testid="create-ip-company" /></Field>
          <Field label="Contact Name"><input value={form.contact_name} onChange={fld('contact_name')} className="ip" data-testid="create-ip-contact-name" /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={fld('email')} className="ip" data-testid="create-ip-email" /></Field>
          <Field label="Phone"><input value={form.phone} onChange={fld('phone')} className="ip" data-testid="create-ip-phone" /></Field>
          <Field label="Website"><input value={form.website} onChange={fld('website')} className="ip" /></Field>
          <Field label="Country"><input value={form.country} onChange={fld('country')} className="ip" /></Field>
          <Field label="Stage">
            <select value={form.stage} onChange={fld('stage')} className="ip" data-testid="create-ip-stage">
              {(enums?.stages || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Language">
            <select value={form.language} onChange={fld('language')} className="ip" data-testid="create-ip-language">
              {(enums?.languages || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Segment"><input value={form.segment} onChange={fld('segment')} className="ip" /></Field>
          <Field label="Import Batch"><input value={form.import_batch} onChange={fld('import_batch')} className="ip" placeholder="e.g. Caribbean Outreach Jun 2026" /></Field>
        </div>
        <div className="mt-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.email_opt_in} onChange={(e) => setForm({ ...form, email_opt_in: e.target.checked })} data-testid="create-ip-opt-in" />
            <span className="text-white/70 text-sm">Email Opt-In</span>
          </label>
        </div>
        <Field label="Notes"><textarea value={form.notes} onChange={fld('notes')} rows={3} className="ip mt-3" /></Field>
        <div className="text-[11px] text-white/40 mt-2">
          If a matching customer already exists (same email, or same company + website/phone), the new info will be merged into the existing record.
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 text-sm bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/30 disabled:opacity-40" data-testid="create-save-btn">
            {saving && <Loader2 className="w-4 h-4 inline mr-1 animate-spin" />}
            Create
          </button>
        </div>
        <style>{`.ip { width:100%; padding:.5rem .75rem; background:#000; border:1px solid rgba(255,255,255,.10); color:#fff; font-size:.875rem; outline:none; } .ip:focus { border-color:rgba(0,212,255,.5); }`}</style>
      </div>
    </div>
  );
};

// ─── Backfill control panel ──────────────────────────────────────────
const BackfillPanel = ({ onCompleted }) => {
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [batchLabel, setBatchLabel] = useState('');
  const [dangerOpen, setDangerOpen] = useState(false);
  const [dryRunDone, setDryRunDone] = useState(false);

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await previewCustomerBackfill();
      setPreview(r.data);
      setDryRunDone(true);
      toast.success(`Preview: would insert ${r.data.totals.inserted}, merge ${r.data.totals.merged}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    finally { setPreviewing(false); }
  };

  const runLive = async ({ confirmation_phrase, acknowledged }) => {
    setRunning(true);
    try {
      const r = await runCustomerBackfill({
        dry_run: false,
        batch_label: batchLabel,
        confirmation_phrase,
        acknowledged,
      });
      toast.success(`Backfill complete: inserted ${r.data.totals.inserted}, merged ${r.data.totals.merged}`);
      setDangerOpen(false);
      onCompleted?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Backfill failed'); }
    finally { setRunning(false); }
  };

  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-4" data-testid="backfill-panel">
      <div className="flex items-center gap-2 mb-3">
        <Database className="w-4 h-4 text-[#00D4FF]" />
        <span className="text-sm font-semibold text-white">Backfill from Legacy Sources</span>
      </div>
      <div className="text-[11px] text-white/50 mb-3 leading-relaxed">
        Re-scans contacts, prospects, subscribers, buyers, sourcing requests, and quotes; dedupes by email → company+website → company+phone; merges sources into existing customers. Idempotent — safe to run repeatedly.
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          value={batchLabel}
          onChange={(e) => setBatchLabel(e.target.value)}
          placeholder="Optional batch label (applies to fresh inserts only)"
          className="flex-1 min-w-[260px] px-3 py-1.5 bg-black border border-white/10 text-white text-xs"
          data-testid="backfill-batch-label"
        />
        <button onClick={runPreview} disabled={previewing} className="px-3 py-1.5 text-xs border border-white/20 text-white/80 hover:bg-white/5 disabled:opacity-40 flex items-center gap-1.5" data-testid="backfill-dry-run-btn">
          {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          Dry Run
        </button>
        <button
          onClick={() => setDangerOpen(true)}
          disabled={running || !dryRunDone}
          title={!dryRunDone ? 'Run a Dry Run first before executing live backfill' : 'Run live backfill'}
          className="px-3 py-1.5 text-xs border border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          data-testid="backfill-live-btn"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          Live Backfill
        </button>
        {!dryRunDone && (
          <span className="text-[10px] text-yellow-500/70">← Run Dry Run first</span>
        )}
      </div>
      {preview && (
        <div className="text-[11px] text-white/70 bg-black/40 border border-white/5 p-2.5 font-mono" data-testid="backfill-preview-summary">
          <div className="mb-1">
            Scanned <b className="text-white">{preview.totals.scanned}</b> ·
            Would insert <b className="text-[#00D4FF]">{preview.totals.inserted}</b> ·
            Would merge <b className="text-yellow-400">{preview.totals.merged}</b> ·
            Skipped <b className="text-white/60">{preview.totals.skipped}</b>
          </div>
          <div className="text-white/40">
            {Object.entries(preview.by_source).map(([k, v]) => `${k}: ${v.inserted}+${v.merged}/${v.scanned}`).join('  ·  ')}
          </div>
        </div>
      )}

      <DangerConfirmModal
        open={dangerOpen}
        onClose={() => !running && setDangerOpen(false)}
        onConfirm={runLive}
        title="Run Live Customer Backfill"
        description={`This will write to the customers collection. It is idempotent — running it again won't duplicate rows — but it may modify existing records (add sources, upgrade stages).${preview ? `\n\nLast dry-run: insert ${preview.totals.inserted} · merge ${preview.totals.merged} · skip ${preview.totals.skipped}` : ''}${batchLabel ? `\n\nBatch label: ${batchLabel}` : ''}`}
        confirmPhrase="RUN LIVE CUSTOMER BACKFILL"
        loading={running}
      />
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────
export const AdminCustomers = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [enums, setEnums] = useState(null);
  const [stats, setStats] = useState(null);
  // filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stage, setStage] = useState('');
  const [language, setLanguage] = useState('');
  const [source, setSource] = useState('');
  const [segment, setSegment] = useState('');
  const [importBatch, setImportBatch] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(''); // '', 'true', 'false'
  // drawer + create
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => {
    const p = { page: 1, per_page: PER_PAGE };
    if (debouncedSearch) p.search = debouncedSearch;
    if (stage) p.stage = stage;
    if (language) p.language = language;
    if (source) p.source = source;
    if (segment) p.segment = segment;
    if (importBatch) p.import_batch = importBatch;
    if (emailOptIn !== '') p.email_opt_in = emailOptIn;
    return p;
  }, [debouncedSearch, stage, language, source, segment, importBatch, emailOptIn]);

  const fetchRows = useCallback(async (pageNum = 1, { append = false } = {}) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const r = await listCustomers({ ...params, page: pageNum });
      const body = r.data || {};
      setRows((prev) => append ? [...prev, ...(body.rows || [])] : (body.rows || []));
      setPage(body.page || 1);
      setTotal(body.total_count || 0);
      setTotalPages(body.total_pages || 1);
    } catch (e) { toast.error('Failed to load customers'); }
    finally { if (append) setLoadingMore(false); else setLoading(false); }
  }, [params]);

  const fetchStats = useCallback(async () => {
    try { const r = await getCustomerStats(); setStats(r.data); } catch (e) { /* keep prior */ }
  }, []);

  useEffect(() => {
    (async () => {
      try { const r = await getCustomerEnums(); setEnums(r.data); } catch (e) { /* fallback to empty */ }
    })();
    fetchStats();
  }, [fetchStats]);

  useEffect(() => { fetchRows(1, { append: false }); }, [fetchRows]);

  const loadMore = () => { if (page < totalPages && !loadingMore) fetchRows(page + 1, { append: true }); };

  const handleExport = () => {
    // Open in new tab; browser handles XLSX download via Content-Disposition
    const qs = { ...params };
    delete qs.page; delete qs.per_page;
    const url = exportCustomersXlsxUrl(qs);
    // Need auth header for download — fetch with token and create blob.
    const token = localStorage.getItem('luxortek_admin_token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `customers_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success('Export downloaded');
      })
      .catch(() => toast.error('Export failed'));
  };

  const onRowSaved = (updated) => {
    setRows(rows.map(r => r.id === updated.id ? updated : r));
    fetchStats();
  };

  const refreshAll = () => { fetchRows(1, { append: false }); fetchStats(); };

  return (
    <div className="px-6 py-4 min-h-screen bg-black text-white">
      <Toaster theme="dark" position="bottom-right" />
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-[#00D4FF]" /> Customers</h1>
          <div className="text-[11px] text-white/50 mt-1">Master customer database. Phase 2 — admin UI only. Old sections still available for cross-checking.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refreshAll} className="px-3 py-1.5 text-xs border border-white/20 text-white/70 hover:bg-white/5 flex items-center gap-1.5" data-testid="refresh-btn">
            <RefreshCcw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button onClick={handleExport} className="px-3 py-1.5 text-xs border border-white/20 text-white/80 hover:bg-white/5 flex items-center gap-1.5" data-testid="export-xlsx-btn">
            <Download className="w-3.5 h-3.5" /> Export XLSX
          </button>
          <button onClick={() => setCreateOpen(true)} className="px-4 py-1.5 text-xs bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/30 flex items-center gap-1.5" data-testid="create-customer-btn">
            <Plus className="w-3.5 h-3.5" /> New Customer
          </button>
        </div>
      </div>

      {/* KPI cards — Round 1: 6 tiles in ONE row on desktop (was 6+2).
          Below-fold breakdowns moved next to the KPI strip in a 4-col
          grid on lg+ for a single dense header band. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-2 mb-3" data-testid="kpis-row">
        <Kpi label="Total" value={stats?.total} testid="kpi-total" />
        <Kpi label="Opted-In" value={stats?.opted_in} testid="kpi-opted-in" />
        <Kpi label="No Email" value={stats?.no_email} testid="kpi-no-email" />
        <Kpi label="Never Contacted" value={stats?.never_contacted} testid="kpi-never-contacted" />
        <Kpi label="Contacted ≤30d" value={stats?.contacted_within_30d} testid="kpi-contacted-30d" />
        <Kpi label="Not Contacted 30+d" value={stats?.not_contacted_30d} testid="kpi-not-contacted-30d" />
        <div className="px-3 py-2 bg-[#0A0A0A] border border-white/10 col-span-2 lg:col-span-1" data-testid="kpi-by-language">
          <div className="text-[9px] uppercase tracking-widest text-white/40">By Language</div>
          <div className="text-[10px] text-white/70 font-mono mt-0.5 leading-tight">
            {(stats?.by_language || []).slice(0, 4).map(b => <div key={b.language}>{b.language}: <span className="text-white">{b.count}</span></div>)}
          </div>
        </div>
        <div className="px-3 py-2 bg-[#0A0A0A] border border-white/10 col-span-2 lg:col-span-1" data-testid="kpi-by-stage">
          <div className="text-[9px] uppercase tracking-widest text-white/40">By Stage</div>
          <div className="text-[10px] text-white/70 font-mono mt-0.5 leading-tight">
            {(stats?.by_stage || []).slice(0, 4).map(b => <div key={b.stage}>{b.stage}: <span className="text-white">{b.count}</span></div>)}
          </div>
        </div>
      </div>

      {/* Backfill panel */}
      <div className="mb-5">
        <BackfillPanel onCompleted={refreshAll} />
      </div>

      {/* Filters — Round 1: tighter padding so the table starts higher
          on the page. Combined with the compact KPI strip this lifts
          ~1 extra row above the fold at 1920×1080. */}
      <div className="border border-white/10 bg-[#0A0A0A] px-4 py-2 mb-2" data-testid="filters-panel">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Search</div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Email, company, contact, phone, website..."
                className="w-full pl-8 pr-3 py-1.5 bg-black border border-white/10 text-white text-xs"
                data-testid="search-input"
              />
            </div>
          </div>
          <Sel label="Stage" value={stage} onChange={setStage} options={enums?.stages} testid="filter-stage" />
          <Sel label="Language" value={language} onChange={setLanguage} options={enums?.languages} testid="filter-language" />
          <Sel label="Source" value={source} onChange={setSource} options={enums?.sources} testid="filter-source" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Segment</div>
            <input value={segment} onChange={(e) => setSegment(e.target.value)} className="px-3 py-1.5 bg-black border border-white/10 text-white text-xs" data-testid="filter-segment" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Import Batch</div>
            <input value={importBatch} onChange={(e) => setImportBatch(e.target.value)} className="px-3 py-1.5 bg-black border border-white/10 text-white text-xs" data-testid="filter-batch" placeholder="exact match" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Opt-In</div>
            <select value={emailOptIn} onChange={(e) => setEmailOptIn(e.target.value)} className="px-3 py-1.5 bg-black border border-white/10 text-white text-xs" data-testid="filter-opt-in">
              <option value="">Any</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="border border-white/10 bg-[#0A0A0A]" data-testid="customers-table-wrap">
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
          <span className="text-[11px] text-white/60 font-mono" data-testid="result-count">
            Showing {rows.length.toLocaleString()} of {total.toLocaleString()} customers
          </span>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-white/40" />}
        </div>
        <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="sticky top-0 z-10 bg-black/90 backdrop-blur-sm text-[10px] uppercase tracking-widest text-white/40 shadow-[0_1px_0_rgba(255,255,255,0.05)]">
              <tr>
                <th className="text-left px-3 py-2">Company</th>
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Phone</th>
                <th className="text-left px-3 py-2">Lang</th>
                <th className="text-left px-3 py-2">Stage</th>
                <th className="text-left px-3 py-2">Sources</th>
                <th className="text-left px-3 py-2">Segment</th>
                <th className="text-left px-3 py-2">Batch</th>
                <th className="text-center px-3 py-2">Opt-In</th>
                <th className="text-left px-3 py-2">Last Contact</th>
                <th className="text-left px-3 py-2">Last Camp.</th>
                <th className="text-center px-3 py-2" title="Total Campaigns Received">#</th>
                <th className="text-center px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={14} className="px-3 py-12 text-center text-white/30">No customers match the current filters.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => setEditing(r)} data-testid={`row-${r.id}`}>
                  <td className="px-3 py-1 text-white max-w-[220px] truncate" title={r.company}>{r.company || '\u2014'}</td>
                  <td className="px-3 py-1 text-white/80">{r.contact_name || '\u2014'}</td>
                  <td className="px-3 py-1 text-white/70 font-mono text-[11px] max-w-[200px] truncate" title={r.email}>{r.email || '\u2014'}</td>
                  <td className="px-3 py-1 text-white/70 font-mono text-[11px]">{r.phone || '\u2014'}</td>
                  <td className="px-3 py-1 text-white/70">{r.language || '\u2014'}</td>
                  <td className="px-3 py-1"><StageBadge stage={r.stage} /></td>
                  <td className="px-3 py-1 text-[10px] text-white/60 max-w-[180px] truncate" title={(r.sources || []).join(', ')}>{(r.sources || []).join(', ') || '\u2014'}</td>
                  <td className="px-3 py-1 text-white/60 text-[11px]">{r.segment || '\u2014'}</td>
                  <td className="px-3 py-1 text-[#00D4FF] text-[11px]">{r.import_batch || '\u2014'}</td>
                  <td className="px-3 py-1 text-center">
                    {r.email_opt_in
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
                      : <X className="w-4 h-4 text-red-400 inline" />}
                  </td>
                  <td className="px-3 py-1 text-white/60 text-[11px]">{formatDate(r.last_contact)}</td>
                  <td className="px-3 py-1 text-white/60 text-[11px]">{formatDate(r.last_campaign_sent)}</td>
                  <td className="px-3 py-1 text-center text-white/70 text-[11px] font-mono" data-testid="cell-total-campaigns">{r.total_campaigns_received ?? 0}</td>
                  <td className="px-3 py-1 text-center text-white/30 hover:text-white"><ChevronRight className="w-4 h-4 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {rows.length < total && (
        <div className="mt-2 flex items-center justify-center gap-3 py-3 border border-white/5 bg-[#050505]" data-testid="load-more-bar">
          <span className="text-[11px] text-white/40 font-mono">{rows.length.toLocaleString()} of {total.toLocaleString()} loaded</span>
          <button onClick={loadMore} disabled={loadingMore} className="px-4 py-1.5 text-xs border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/10 disabled:opacity-40 flex items-center gap-1.5" data-testid="load-more-btn">
            {loadingMore ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Load More
          </button>
        </div>
      )}

      <EditDrawer
        open={!!editing}
        customer={editing}
        enums={enums}
        onClose={() => setEditing(null)}
        onSaved={onRowSaved}
      />
      <CreateModal
        open={createOpen}
        enums={enums}
        onClose={() => setCreateOpen(false)}
        onCreated={() => refreshAll()}
      />
    </div>
  );
};

const Sel = ({ label, value, onChange, options = [], testid }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="px-3 py-1.5 bg-black border border-white/10 text-white text-xs" data-testid={testid}>
      <option value="">All</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const STAGE_COLORS = {
  New:         'border-white/20 text-white/70',
  Lead:        'border-blue-400/40 text-blue-400 bg-blue-400/10',
  Prospect:    'border-purple-400/40 text-purple-400 bg-purple-400/10',
  Subscriber:  'border-cyan-400/40 text-cyan-400 bg-cyan-400/10',
  Contacted:   'border-sky-400/40 text-sky-400 bg-sky-400/10',
  Interested:  'border-yellow-400/40 text-yellow-400 bg-yellow-400/10',
  'Quote Sent':'border-orange-400/40 text-orange-400 bg-orange-400/10',
  Negotiating: 'border-amber-400/40 text-amber-400 bg-amber-400/10',
  Customer:    'border-emerald-400/40 text-emerald-400 bg-emerald-400/10',
  Lost:        'border-red-400/40 text-red-400 bg-red-400/10',
};
const StageBadge = ({ stage }) => (
  <span className={`inline-block px-1.5 py-0.5 text-[10px] border ${STAGE_COLORS[stage] || 'border-white/10 text-white/50'} font-mono`}>
    {stage || '\u2014'}
  </span>
);

export default AdminCustomers;
